import { useState, useEffect } from "react";
import { normalizeUrl } from "@/lib/utils";
import { format } from "date-fns";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Card, CardContent } from "@/components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Checkbox } from "@/components/ui/checkbox";
import { Switch } from "@/components/ui/switch";
import { toast } from "sonner";
import { Pencil, Trash2, Search, Plus, UserPlus, Copy, Eye, EyeOff, ShieldCheck, Shield, EyeOff as HiddenIcon, KeyRound, CheckCircle2, AlertTriangle, CalendarCheck } from "lucide-react";
import ImageUploadWithCrop from "@/components/shared/ImageUploadWithCrop";
import { memberCategories } from "@/data/members";

interface Member {
  id: string;
  name: string;
  company: string;
  title: string;
  description: string;
  about: string | null;
  website: string | null;
  phone: string | null;
  email: string | null;
  category: string;
  image: string | null;
  linkedin: string | null;
  facebook: string | null;
  chapter: "washington" | "union" | "all";
  additional_chapters: ("washington" | "union" | "all")[] | null;
  user_id: string | null;
  joined_date: string | null;
  is_hidden: boolean;
  is_chapter_president?: boolean;
  is_exempt_from_dues?: boolean;
  next_renewal_date?: string | null;
}

const emptyMember = {
  name: "",
  company: "",
  title: "",
  description: "",
  about: "",
  website: "",
  phone: "",
  email: "",
  category: "",
  image: "",
  linkedin: "",
  facebook: "",
  chapter: "washington" as "washington" | "union" | "all",
  additional_chapters: [] as ("washington" | "union" | "all")[],
  joined_date: "",
  is_hidden: false,
  is_chapter_president: false,
  is_exempt_from_dues: false,
};

const generateTempPassword = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnpqrstuvwxyz23456789";
  let password = "";
  for (let i = 0; i < 12; i++) {
    password += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return password;
};

const AdminMemberList = () => {
  const [members, setMembers] = useState<Member[]>([]);
  const [adminUserIds, setAdminUserIds] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [chapterFilter, setChapterFilter] = useState<string>("all-chapters");
  const [editingMember, setEditingMember] = useState<Member | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [formData, setFormData] = useState(emptyMember);
  const [saving, setSaving] = useState(false);
  const [createAccount, setCreateAccount] = useState(false);
  const [tempPassword, setTempPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [resetMember, setResetMember] = useState<Member | null>(null);
  const [resetting, setResetting] = useState(false);
  const [resetConfirmedPassword, setResetConfirmedPassword] = useState<string | null>(null);
  const [resetCopied, setResetCopied] = useState(false);

  const openResetPassword = (member: Member) => {
    setResetMember(member);
    setResetConfirmedPassword(null);
    setResetCopied(false);
  };

  const closeResetDialog = () => {
    setResetMember(null);
    setResetConfirmedPassword(null);
    setResetCopied(false);
  };

  const handleResetPassword = async () => {
    if (!resetMember?.user_id) return;
    const newPassword = generateTempPassword();
    setResetting(true);
    try {
      const { data, error } = await supabase.functions.invoke("admin-reset-member-password", {
        body: { user_id: resetMember.user_id, temp_password: newPassword },
      });
      if (error) throw error;
      if (data?.error) throw new Error(data.error);
      // Only reveal password AFTER backend confirms success
      setResetConfirmedPassword(newPassword);
      toast.success(`Password reset for ${resetMember.name}`);
    } catch (err: any) {
      toast.error("Error resetting password: " + (err?.message || "Unknown error"));
    } finally {
      setResetting(false);
    }
  };

  const copyConfirmedPassword = () => {
    if (!resetConfirmedPassword) return;
    navigator.clipboard.writeText(resetConfirmedPassword);
    setResetCopied(true);
    toast.success("Password copied to clipboard");
    setTimeout(() => setResetCopied(false), 2500);
  };

  useEffect(() => {
    fetchMembers();
    fetchAdminIds();
  }, []);

  const fetchMembers = async () => {
    const { data, error } = await supabase.from("members").select("*").order("name");
    if (error) {
      toast.error("Error loading members");
      return;
    }
    setMembers(data || []);
    setLoading(false);
  };

  const fetchAdminIds = async () => {
    const { data } = await supabase.from("user_roles").select("user_id").eq("role", "admin");
    if (data) {
      setAdminUserIds(new Set(data.map((r) => r.user_id)));
    }
  };

  const toggleAdmin = async (member: Member) => {
    if (!member.user_id) {
      toast.error("This member doesn't have a linked account");
      return;
    }
    const isCurrentlyAdmin = adminUserIds.has(member.user_id);
    const action = isCurrentlyAdmin ? "remove admin from" : "make admin";
    if (!confirm(`Are you sure you want to ${action} ${member.name}?`)) return;

    try {
      if (isCurrentlyAdmin) {
        const { error } = await supabase
          .from("user_roles")
          .delete()
          .eq("user_id", member.user_id)
          .eq("role", "admin");
        if (error) throw error;
        toast.success(`${member.name} is no longer an admin`);
      } else {
        const { error } = await supabase
          .from("user_roles")
          .insert({ user_id: member.user_id, role: "admin" });
        if (error) throw error;
        toast.success(`${member.name} is now an admin`);
      }
      fetchAdminIds();
    } catch (error: any) {
      toast.error("Error updating role: " + error.message);
    }
  };

  const handleEdit = (member: Member) => {
    setEditingMember(member);
    setIsCreating(false);
    setCreateAccount(false);
    setFormData({
      name: member.name,
      company: member.company,
      title: member.title,
      description: member.description,
      about: member.about || "",
      website: member.website || "",
      phone: member.phone || "",
      email: member.email || "",
      category: member.category,
      image: member.image || "",
      linkedin: member.linkedin || "",
      facebook: member.facebook || "",
      chapter: member.chapter as "washington" | "union" | "all",
      additional_chapters: (member.additional_chapters || []) as ("washington" | "union" | "all")[],
      joined_date: member.joined_date || "",
      is_hidden: member.is_hidden ?? false,
      is_chapter_president: member.is_chapter_president ?? false,
      is_exempt_from_dues: member.is_exempt_from_dues ?? false,
    });
  };

  const handleCreate = () => {
    setEditingMember(null);
    setIsCreating(true);
    setFormData(emptyMember);
    setCreateAccount(false);
    setTempPassword(generateTempPassword());
    setShowPassword(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        name: formData.name,
        company: formData.company,
        title: formData.title,
        description: formData.description,
        about: formData.about || null,
        website: normalizeUrl(formData.website),
        phone: formData.phone || null,
        email: formData.email || null,
        category: formData.category,
        image: formData.image || null,
        linkedin: normalizeUrl(formData.linkedin),
        facebook: normalizeUrl(formData.facebook),
        chapter: formData.chapter,
        additional_chapters: (formData.additional_chapters || []).filter((c) => c !== formData.chapter),
        joined_date: formData.joined_date || null,
        is_hidden: formData.is_hidden,
        is_chapter_president: formData.is_chapter_president,
        is_exempt_from_dues: formData.is_exempt_from_dues,
      };

      let userId = editingMember?.user_id || null;

      // If creating account, call edge function first
      if (isCreating && createAccount && formData.email) {
        const { data, error: fnError } = await supabase.functions.invoke("create-member-user", {
          body: { email: formData.email, temp_password: tempPassword },
        });

        if (fnError) throw new Error(fnError.message || "Failed to create user account");
        if (data?.error) throw new Error(data.error);

        userId = data.user_id;
        toast.success(`Account created! Temporary password (use on Sign In screen): ${tempPassword}`, { duration: 15000 });
      }

      if (isCreating) {
        const { error } = await supabase.from("members").insert([{ ...payload, user_id: userId }]);
        if (error) throw error;
        toast.success("Member created successfully!");
      } else if (editingMember) {
        const { error } = await supabase.from("members").update(payload).eq("id", editingMember.id);
        if (error) throw error;
        const newEmail = (formData.email || "").trim().toLowerCase();
        const oldEmail = (editingMember.email || "").trim().toLowerCase();
        const linkedUserId = (editingMember as any).user_id as string | null | undefined;
        if (linkedUserId && newEmail && newEmail !== oldEmail) {
          const { data, error: fnError } = await supabase.functions.invoke("admin-update-member-email", {
            body: { user_id: linkedUserId, email: newEmail },
          });
          if (fnError || (data as any)?.error) {
            toast.error(`Profile saved, but login email wasn't updated: ${(data as any)?.error || fnError?.message}`);
          } else {
            toast.success(`Login email also changed to ${newEmail}`);
          }
        }
        toast.success("Member updated successfully!");
      }

      setEditingMember(null);
      setIsCreating(false);
      fetchMembers();
    } catch (error: any) {
      toast.error("Error saving member: " + error.message);
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (member: Member) => {
    if (!confirm(`Are you sure you want to delete ${member.name}?`)) return;

    const { error } = await supabase.from("members").delete().eq("id", member.id);
    if (error) {
      toast.error("Error deleting member: " + error.message);
      return;
    }
    toast.success("Member deleted");
    fetchMembers();
  };

  const handleMarkRenewed = async (member: Member) => {
    const baseStr = member.next_renewal_date || member.joined_date;
    const base = baseStr ? new Date(baseStr + "T00:00:00Z") : new Date();
    base.setUTCFullYear(base.getUTCFullYear() + 1);
    const nextDate = base.toISOString().slice(0, 10);

    const note = window.prompt(
      `Mark ${member.name} as renewed?\n\nThis advances the next renewal date to ${nextDate} and records a manual payment.\n\nOptional note (e.g., "Check #1234", "Cash"):`,
      ""
    );
    if (note === null) return; // cancelled

    const { error: payErr } = await supabase.from("payments").insert({
      member_id: member.id,
      amount: 30000,
      currency: "usd",
      status: "completed",
      mode: "renewal",
      paid_at: new Date().toISOString(),
      failure_message: note ? `manual: ${note}` : "manual",
    });
    if (payErr) {
      toast.error("Failed to record payment: " + payErr.message);
      return;
    }

    const { error: memErr } = await supabase
      .from("members")
      .update({
        next_renewal_date: nextDate,
        membership_status: "good_standing",
        renewal_source: "manual",
      })
      .eq("id", member.id);
    if (memErr) {
      toast.error("Failed to update renewal date: " + memErr.message);
      return;
    }

    toast.success(`${member.name} renewed through ${nextDate}`);
    fetchMembers();
  };

  const handleChange = (field: string, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const filteredMembers = members.filter((m) => {
    const matchesSearch =
      m.name.toLowerCase().includes(search.toLowerCase()) ||
      m.company.toLowerCase().includes(search.toLowerCase());
    const matchesChapter = chapterFilter === "all-chapters" || m.chapter === chapterFilter;
    return matchesSearch && matchesChapter;
  });

  if (loading) return <p>Loading members...</p>;

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
        <div className="flex flex-col sm:flex-row gap-2 flex-1">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Search members..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="pl-9"
            />
          </div>
          <Select value={chapterFilter} onValueChange={setChapterFilter}>
            <SelectTrigger className="w-[180px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all-chapters">All Chapters</SelectItem>
              <SelectItem value="washington">Washington</SelectItem>
              <SelectItem value="union">Union</SelectItem>
            </SelectContent>
          </Select>
        </div>
        <Button onClick={handleCreate}>
          <Plus className="h-4 w-4 mr-2" />
          Add Member
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Company</TableHead>
                <TableHead className="hidden md:table-cell">Category</TableHead>
                <TableHead>Chapter</TableHead>
                <TableHead className="hidden sm:table-cell">Account</TableHead>
                <TableHead className="hidden lg:table-cell">Joined</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {filteredMembers.map((member) => (
                <TableRow key={member.id}>
                  <TableCell className="font-medium">
                    <span className="flex items-center gap-1.5 flex-wrap">
                      {member.name}
                      {member.user_id && adminUserIds.has(member.user_id) && (
                        <ShieldCheck className="h-4 w-4 text-amber-500" />
                      )}
                      {member.is_hidden && (
                        <Badge variant="secondary" className="gap-1 text-xs">
                          <HiddenIcon className="h-3 w-3" />
                          Hidden
                        </Badge>
                      )}
                      {member.is_exempt_from_dues && (
                        <Badge variant="outline" className="text-xs border-emerald-500/50 text-emerald-700 dark:text-emerald-400">
                          Exempt
                        </Badge>
                      )}
                    </span>
                  </TableCell>
                  <TableCell>{member.company}</TableCell>
                  <TableCell className="hidden md:table-cell">{member.category}</TableCell>
                  <TableCell>
                    <Badge variant="outline" className="capitalize">
                      {member.chapter}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden sm:table-cell">
                    <Badge variant={member.user_id ? "default" : "secondary"} className={member.user_id ? "bg-green-600 hover:bg-green-700" : ""}>
                      {member.user_id ? "Linked" : "None"}
                    </Badge>
                  </TableCell>
                  <TableCell className="hidden lg:table-cell text-sm text-muted-foreground">
                    {member.joined_date ? format(new Date(member.joined_date + "T00:00:00"), "MM/yyyy") : "—"}
                  </TableCell>
                  <TableCell className="text-right space-x-1">
                    {member.user_id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => toggleAdmin(member)}
                        title={adminUserIds.has(member.user_id) ? "Remove admin" : "Make admin"}
                      >
                        {adminUserIds.has(member.user_id) ? (
                          <ShieldCheck className="h-4 w-4 text-amber-500" />
                        ) : (
                          <Shield className="h-4 w-4 text-muted-foreground" />
                        )}
                      </Button>
                    )}
                    {member.user_id && (
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={() => openResetPassword(member)}
                        title="Reset password (set temp password)"
                      >
                        <KeyRound className="h-4 w-4 text-muted-foreground" />
                      </Button>
                    )}
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => handleMarkRenewed(member)}
                      title="Mark as renewed (advance renewal date 1 year)"
                    >
                      <CalendarCheck className="h-4 w-4 text-emerald-600" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleEdit(member)}>
                      <Pencil className="h-4 w-4" />
                    </Button>
                    <Button variant="ghost" size="icon" onClick={() => handleDelete(member)}>
                      <Trash2 className="h-4 w-4 text-destructive" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
              {filteredMembers.length === 0 && (
                <TableRow>
                  <TableCell colSpan={7} className="text-center text-muted-foreground py-8">
                    No members found
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      <Dialog open={!!editingMember || isCreating} onOpenChange={(open) => { if (!open) { setEditingMember(null); setIsCreating(false); } }}>
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{isCreating ? "Add New Member" : `Edit: ${editingMember?.name}`}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 pt-4">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Name *</Label>
                <Input value={formData.name} onChange={(e) => handleChange("name", e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Company *</Label>
                <Input value={formData.company} onChange={(e) => handleChange("company", e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Title *</Label>
                <Input value={formData.title} onChange={(e) => handleChange("title", e.target.value)} required />
              </div>
              <div className="space-y-2">
                <Label>Category *</Label>
                <Select value={formData.category} onValueChange={(v) => handleChange("category", v)}>
                  <SelectTrigger>
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {memberCategories.filter((c) => c !== "All").map((cat) => (
                      <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Chapter *</Label>
                <Select value={formData.chapter} onValueChange={(v) => handleChange("chapter", v)}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="washington">Washington</SelectItem>
                    <SelectItem value="union">Union</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2 md:col-span-2">
                <Label>Additional Chapters</Label>
                <div className="rounded-md border p-3 space-y-2">
                  <p className="text-xs text-muted-foreground">
                    Optional. For dual-chapter members (e.g., founders serving multiple chapters). They'll appear in both directories and can send/receive referrals across selected chapters.
                  </p>
                  <div className="flex flex-wrap gap-4 pt-1">
                    {(["washington", "union"] as const)
                      .filter((c) => c !== formData.chapter)
                      .map((c) => (
                        <label key={c} className="flex items-center gap-2 text-sm cursor-pointer">
                          <Checkbox
                            checked={formData.additional_chapters?.includes(c) || false}
                            onCheckedChange={(checked) => {
                              setFormData((prev) => {
                                const current = prev.additional_chapters || [];
                                return {
                                  ...prev,
                                  additional_chapters: checked
                                    ? [...current.filter((x) => x !== c), c]
                                    : current.filter((x) => x !== c),
                                };
                              });
                            }}
                          />
                          <span className="capitalize">{c}</span>
                        </label>
                      ))}
                  </div>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Email</Label>
                <Input type="email" value={formData.email} onChange={(e) => handleChange("email", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Phone</Label>
                <Input value={formData.phone} onChange={(e) => handleChange("phone", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Website</Label>
                <Input value={formData.website} onChange={(e) => handleChange("website", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>LinkedIn</Label>
                <Input value={formData.linkedin} onChange={(e) => handleChange("linkedin", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Facebook</Label>
                <Input value={formData.facebook} onChange={(e) => handleChange("facebook", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Joined Date</Label>
                <Input type="date" value={formData.joined_date} onChange={(e) => handleChange("joined_date", e.target.value)} />
              </div>
              <div className="md:col-span-2 flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="is_hidden" className="cursor-pointer">Hidden from public site</Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, this member is excluded from the public directory, member counts, and referral recipient dropdowns. Useful for test accounts.
                  </p>
                </div>
                <Switch
                  id="is_hidden"
                  checked={formData.is_hidden}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_hidden: checked }))}
                />
              </div>
              <div className="md:col-span-2 flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="is_chapter_president" className="cursor-pointer">Chapter president</Label>
                  <p className="text-xs text-muted-foreground">
                    When enabled, this member is CC'd on renewal reminder emails for everyone in their chapter.
                  </p>
                </div>
                <Switch
                  id="is_chapter_president"
                  checked={formData.is_chapter_president}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_chapter_president: checked }))}
                />
              </div>
              <div className="md:col-span-2 flex items-start justify-between gap-4 rounded-md border p-3">
                <div className="space-y-0.5">
                  <Label htmlFor="is_exempt_from_dues" className="cursor-pointer">Exempt from dues</Label>
                  <p className="text-xs text-muted-foreground">
                    Non-profits and volunteer positions. Excluded from renewal reminders and payment prompts.
                  </p>
                </div>
                <Switch
                  id="is_exempt_from_dues"
                  checked={formData.is_exempt_from_dues}
                  onCheckedChange={(checked) => setFormData((prev) => ({ ...prev, is_exempt_from_dues: checked }))}
                />
              </div>
              <div className="md:col-span-2">
                <ImageUploadWithCrop
                  currentImage={formData.image}
                  fallbackText={formData.company}
                  userId={editingMember?.user_id || undefined}
                  onImageChange={(url) => handleChange("image", url)}
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label>Description *</Label>
              <Textarea value={formData.description} onChange={(e) => handleChange("description", e.target.value)} rows={3} required />
            </div>
            <div className="space-y-2">
              <Label>About</Label>
              <Textarea value={formData.about} onChange={(e) => handleChange("about", e.target.value)} rows={4} />
            </div>

            {/* Create Account Section - only shown when adding new members */}
            {isCreating && (
              <Card className="border-dashed">
                <CardContent className="pt-4 space-y-3">
                  <div className="flex items-center space-x-2">
                    <Checkbox
                      id="createAccount"
                      checked={createAccount}
                      onCheckedChange={(checked) => setCreateAccount(checked === true)}
                    />
                    <Label htmlFor="createAccount" className="flex items-center gap-2 cursor-pointer">
                      <UserPlus className="h-4 w-4" />
                      Create login account for this member
                    </Label>
                  </div>
                  {createAccount && (
                    <div className="space-y-2 pl-6">
                      <Label>Temporary Password (enter on the Sign In screen)</Label>
                      <div className="flex gap-2">
                        <div className="relative flex-1">
                          <Input
                            type={showPassword ? "text" : "password"}
                            value={tempPassword}
                            onChange={(e) => setTempPassword(e.target.value)}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-2 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                          >
                            {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                          </button>
                        </div>
                        <Button
                          type="button"
                          variant="outline"
                          size="icon"
                          onClick={() => {
                            navigator.clipboard.writeText(tempPassword);
                            toast.success("Password copied!");
                          }}
                        >
                          <Copy className="h-4 w-4" />
                        </Button>
                        <Button
                          type="button"
                          variant="outline"
                          onClick={() => setTempPassword(generateTempPassword())}
                        >
                          Regenerate
                        </Button>
                      </div>
                      <p className="text-xs text-muted-foreground">
                        Share this temporary password with the member. They sign in with their email + this password on the Sign In screen (not the "Forgot password" code screen), and will be prompted to set a new password on first login.
                      </p>
                    </div>
                  )}
                </CardContent>
              </Card>
            )}

            <div className="flex gap-2 justify-end">
              <Button variant="outline" onClick={() => { setEditingMember(null); setIsCreating(false); }}>
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving}>
                {saving ? "Saving..." : isCreating ? "Create Member" : "Save Changes"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetMember} onOpenChange={(open) => { if (!open) closeResetDialog(); }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Reset Password: {resetMember?.name}</DialogTitle>
          </DialogHeader>

          {/* State 1: Pre-confirmation — no password shown yet */}
          {!resetConfirmedPassword && (
            <div className="space-y-4 pt-2">
              <div className="rounded-md border border-amber-500/40 bg-amber-500/10 p-3 flex gap-3">
                <AlertTriangle className="h-5 w-5 text-amber-600 dark:text-amber-400 shrink-0 mt-0.5" />
                <div className="text-sm space-y-1">
                  <p className="font-medium">This will replace the member's current password.</p>
                  <p className="text-muted-foreground">
                    A strong temporary password will be generated and set on their account. They'll be required to choose a new password on next login. Share via phone or text — not email.
                  </p>
                </div>
              </div>
              <div className="flex gap-2 justify-end">
                <Button variant="outline" onClick={closeResetDialog} disabled={resetting}>
                  Cancel
                </Button>
                <Button onClick={handleResetPassword} disabled={resetting}>
                  <KeyRound className="h-4 w-4 mr-2" />
                  {resetting ? "Resetting..." : "Generate & Set Temp Password"}
                </Button>
              </div>
            </div>
          )}

          {/* State 2: Success — password is confirmed set on the backend */}
          {resetConfirmedPassword && (
            <div className="space-y-4 pt-2">
              <div className="rounded-md border border-green-500/40 bg-green-500/10 p-3 flex gap-3">
                <CheckCircle2 className="h-5 w-5 text-green-600 dark:text-green-400 shrink-0 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium">Password reset successfully.</p>
                  <p className="text-muted-foreground">
                    Copy the temp password below and share it with the member by phone or text.
                  </p>
                </div>
              </div>
              <div className="space-y-2">
                <Label>Temporary Password</Label>
                <div className="flex gap-2">
                  <Input
                    readOnly
                    value={resetConfirmedPassword}
                    className="font-mono text-base tracking-wider"
                    onFocus={(e) => e.currentTarget.select()}
                  />
                  <Button type="button" variant={resetCopied ? "default" : "outline"} onClick={copyConfirmedPassword}>
                    {resetCopied ? (
                      <><CheckCircle2 className="h-4 w-4 mr-2" />Copied</>
                    ) : (
                      <><Copy className="h-4 w-4 mr-2" />Copy</>
                    )}
                  </Button>
                </div>
                <p className="text-xs text-muted-foreground">
                  This password is only shown once. The member will be required to change it on next login.
                </p>
              </div>
              <div className="flex justify-end">
                <Button onClick={closeResetDialog}>Done</Button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminMemberList;
